/**
 * The landing screen: every session, newest first.
 *
 * IT OPENS ON THE HISTORY RATHER THAN ON A DASHBOARD, deliberately. There is no
 * aggregate on this POC worth more than the list — five hundred sessions a month
 * is not a population you need a chart to understand — and the question people
 * actually arrive with is "that call at 14:32, what happened". A landing screen
 * of averages puts that call two clicks away and buries the one row that matters
 * in a mean.
 *
 * TWO PILLS PER ROW, NOT ONE, and this is the design decision the whole table
 * exists to carry. Session state and evidence state are ORTHOGONAL: a session
 * can end perfectly normally and hold a recording nobody can vouch for, and it
 * can be live-degraded right now and still land a COMPLETE artefact afterwards
 * because the media path never involved us. Collapsing them into a single
 * "status" column would be the exact conflation this architecture was built to
 * be able to see through.
 *
 * POLLING IS CONDITIONAL AND THE CONDITION COMES FROM THE SERVER. `live` in the
 * list response counts LIVE* sessions across the whole population, not this page,
 * so a call that starts while somebody is reading page three still turns the
 * poller on. Deriving it from the rows in hand would leave the list looking
 * finished for as long as the reader stayed off page one. And when nothing is
 * live there is NO interval at all — an idle control panel left open on a
 * Friday should cost nothing over the weekend.
 */
import { useEffect, useState } from 'react'
import { ApiError, sessions, type ListSessionsQuery, type SessionSummary } from '@/api'
import { navigate } from '@/components/app-sidebar'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DataTable,
  PageHeader,
  TableFooter,
  type Column,
  type SortDir,
} from '@/components/data-table'
import {
  AudioPill,
  DASH,
  StatePill,
  TransportIcon,
  fmtCost,
  fmtDuration,
  fmtInt,
  fmtWhen,
} from '@/components/state-pill'

const PAGE = 20
const POLL_MS = 5_000

/** The filter, as the four questions anybody actually asks of this list. */
const FILTERS: { value: string; label: string; query: ListSessionsQuery['state'] }[] = [
  { value: 'all', label: 'All sessions', query: undefined },
  { value: 'live', label: 'Live now', query: 'live' },
  { value: 'ended', label: 'Ended normally', query: 'ENDED_NORMAL' },
  { value: 'unobserved', label: 'Ended, unobserved', query: 'ENDED_UNKNOWN' },
  { value: 'failed', label: 'Failed', query: 'FAILED' },
]

/** The via filter speaks the reader's words; the wire speaks the schema's. */
const VIAS: { value: string; label: string; query: ListSessionsQuery['via'] }[] = [
  { value: 'all', label: 'Web and phone', query: undefined },
  { value: 'web', label: 'Web', query: 'webrtc' },
  { value: 'phone', label: 'Phone', query: 'sip' },
]

/** The four sortable columns, narrowing what DataTable hands back as a string. */
const SORT_KEYS = ['started', 'agent', 'duration', 'cost'] as const
type SortKey = (typeof SORT_KEYS)[number]
const isSortKey = (k: string): k is SortKey => (SORT_KEYS as readonly string[]).includes(k)

/**
 * Local calendar date → instant, at the end the reader means. A date typed into
 * "from" means its first millisecond and one typed into "to" means its last —
 * both in the READER's timezone, which is exactly the knowledge the server does
 * not have and why the conversion happens here.
 */
function dayToIso(day: string, end: 'start' | 'end'): string | undefined {
  if (day === '') return undefined
  const d = new Date(`${day}T${end === 'start' ? '00:00:00.000' : '23:59:59.999'}`)
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString()
}

/**
 * Where the call was initiated from — Cloudflare's geolocation of the request
 * that created the session, so it exists only on the web path. A phone call's
 * origin is its number, not an IP, and pretending otherwise would put a wrong
 * kind of fact in the column; telephony rows get a dash that says so.
 */
function LocationCell({ row }: { row: SessionSummary }) {
  if (row.transport === 'sip') {
    return (
      <span className="faint" title="Phone call — the origin is the caller's number, not a geolocation.">
        {DASH}
      </span>
    )
  }
  const parts = [row.clientCity, row.clientCountry].filter(
    (p): p is string => typeof p === 'string' && p !== '',
  )
  if (parts.length === 0) {
    return (
      <span className="faint" title="Cloudflare did not report a location for the request that started this call.">
        {DASH}
      </span>
    )
  }
  const full = [row.clientCity, row.clientRegion, row.clientCountry]
    .filter((p) => typeof p === 'string' && p !== '')
    .join(', ')
  return (
    <span
      className="truncate"
      title={`${full} — Cloudflare's geolocation of the request that started this call.`}
    >
      {parts.join(', ')}
    </span>
  )
}

export function SessionsPage({
  onCounts,
}: {
  /** Lets the shell's nav badge track the list without a second poller. */
  onCounts?: (counts: { sessions: number; live: number }) => void
}) {
  const [rows, setRows] = useState<SessionSummary[] | null>(null)
  const [total, setTotal] = useState(0)
  const [live, setLive] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [offset, setOffset] = useState(0)
  const [filter, setFilter] = useState('all')
  const [via, setVia] = useState('all')
  // Calendar dates as the <input type="date"> holds them; converted to instants
  // only at request time. Either end may be empty.
  const [fromDay, setFromDay] = useState('')
  const [toDay, setToDay] = useState('')
  const [sort, setSort] = useState<SortKey>('started')
  const [dir, setDir] = useState<SortDir>('desc')
  // Bumped by the poller. A counter rather than a boolean because two
  // consecutive polls must both be distinct dependency values.
  const [epoch, setEpoch] = useState(0)

  const query = FILTERS.find((f) => f.value === filter)?.query
  const viaQuery = VIAS.find((v) => v.value === via)?.query
  const fromIso = dayToIso(fromDay, 'start')
  const toIso = dayToIso(toDay, 'end')
  const anyFilter = filter !== 'all' || via !== 'all' || fromDay !== '' || toDay !== ''

  useEffect(() => {
    const ac = new AbortController()
    sessions
      .list(
        { limit: PAGE, offset, state: query, via: viaQuery, from: fromIso, to: toIso, sort, dir },
        ac.signal,
      )
      .then((res) => {
        setRows(res.rows)
        setTotal(res.total)
        setLive(res.live)
        setError(null)
        onCounts?.({ sessions: res.total, live: res.live })
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return
        // A poll that fails leaves the rows that are already on screen alone.
        // Blanking a readable list because one refresh timed out is worse than
        // showing a list that is five seconds stale, and the banner says so.
        setError(e instanceof ApiError ? e.message : String(e))
      })
    return () => ac.abort()
    // onCounts is a parent callback and is deliberately not a dependency: it is
    // recreated on every parent render and would refetch the list each time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, query, viaQuery, fromIso, toIso, sort, dir, epoch])

  // NO INTERVAL WHEN NOTHING IS LIVE. The dependency is the boolean, not the
  // count, so a second call starting does not restart the timer.
  const anyLive = live > 0
  useEffect(() => {
    if (!anyLive) return
    const id = setInterval(() => setEpoch((n) => n + 1), POLL_MS)
    return () => clearInterval(id)
  }, [anyLive])

  // The four sortable keys are the API's sort vocabulary verbatim — DataTable
  // sends `key` back through onSort, so the column key IS the wire value.
  const columns: Column<SessionSummary>[] = [
    {
      key: 'started',
      header: 'Started',
      width: '11rem',
      sortable: true,
      defaultDir: 'desc',
      cell: (r) => <span className="whitespace-nowrap">{fmtWhen(r.startedAt)}</span>,
    },
    {
      key: 'agent',
      header: 'Agent',
      sortable: true,
      cell: (r) => (
        <span className="truncate">
          {r.agentName ?? <span className="mono faint">{r.agentId}</span>}
        </span>
      ),
    },
    {
      key: 'transport',
      header: 'Via',
      width: '6rem',
      cell: (r) => <TransportIcon transport={r.transport} />,
    },
    {
      key: 'location',
      header: 'Location',
      width: '10rem',
      hideBelow: 'lg',
      cell: (r) => <LocationCell row={r} />,
    },
    {
      key: 'duration',
      header: 'Duration',
      numeric: true,
      width: '6.5rem',
      sortable: true,
      defaultDir: 'desc',
      cell: (r) => fmtDuration(r.durationMs),
    },
    {
      key: 'turns',
      header: 'Turns',
      numeric: true,
      width: '4.5rem',
      hideBelow: 'md',
      cell: (r) => fmtInt(r.turnCount),
    },
    {
      key: 'tools',
      header: 'Tools',
      numeric: true,
      width: '4.5rem',
      hideBelow: 'md',
      cell: (r) => fmtInt(r.toolCallCount),
    },
    {
      key: 'cost',
      header: 'Cost',
      numeric: true,
      width: '6rem',
      hideBelow: 'lg',
      sortable: true,
      defaultDir: 'desc',
      // From the provider's own token accounting, not from our clock. Absent
      // until the usage rows for the session have been reconciled.
      cell: (r) => fmtCost(r.costUsd),
    },
    {
      key: 'state',
      header: 'Session',
      width: '11rem',
      cell: (r) => <StatePill state={r.state} />,
    },
    {
      key: 'audio',
      header: 'Recording',
      width: '8rem',
      // The list answers the simple question — are the audio files stored? —
      // with pending / absent / processed. The evidence claim (COMPLETE,
      // UNVERIFIED, …) lives on the session page, where it can be explained.
      cell: (r) => <AudioPill evidence={r.evidence} recording={r.recording} />,
    },
  ]

  return (
    <div className="min-w-0">
      <PageHeader
        title="Sessions"
        subtitle="Every call this platform has observed, web and phone. Open one to see the per-turn latency decomposition."
        actions={
          <>
            <Select value={filter} onValueChange={(v) => { setFilter(v); setOffset(0) }}>
              <SelectTrigger className="h-8 w-[11rem]" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FILTERS.map((f) => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={via} onValueChange={(v) => { setVia(v); setOffset(0) }}>
              <SelectTrigger className="h-8 w-[10rem]" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VIAS.map((v) => (
                  <SelectItem key={v.value} value={v.value}>
                    {v.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Native date inputs, deliberately: they are the one date control
                every browser localises for free, and this range is two bounds,
                not a calendar experience. Either end may be left empty. */}
            <Input
              type="date"
              aria-label="Started on or after"
              className="h-8 w-[9.5rem]"
              value={fromDay}
              max={toDay || undefined}
              onChange={(e) => { setFromDay(e.target.value); setOffset(0) }}
            />
            <span className="text-xs text-muted-foreground">to</span>
            <Input
              type="date"
              aria-label="Started on or before"
              className="h-8 w-[9.5rem]"
              value={toDay}
              min={fromDay || undefined}
              onChange={(e) => { setToDay(e.target.value); setOffset(0) }}
            />
          </>
        }
      />

      {error && (
        <p className="err mb-4">
          {error}
          {rows !== null && ' — the rows below are the last ones that loaded.'}
        </p>
      )}

      <DataTable
        columns={columns}
        rows={rows}
        keyOf={(r) => r.id}
        onRowClick={(r) => navigate(`/sessions/${r.id}`)}
        sort={sort}
        dir={dir}
        onSort={(key, d) => {
          if (!isSortKey(key)) return
          setSort(key)
          setDir(d)
          // A new order is a new pagination — page 3 of one order is nowhere
          // in particular in another.
          setOffset(0)
        }}
        emptyTitle={anyFilter ? 'Nothing matches these filters' : 'No sessions yet'}
        emptyBody={
          anyFilter ? (
            <>No session matches. Widen the filters — or clear the dates — to see the history.</>
          ) : (
            <>
              Nothing has been recorded yet. Use <strong>Start a call</strong> in the sidebar
              to open a WebRTC session from this browser — it needs microphone permission and
              a desktop Chromium, Firefox or Safari; iOS is blocked deliberately, because
              Safari suspends WebRTC on screen lock and there is no workaround. For the
              phone path, dial the Twilio number configured for this project and a session
              will appear here on its own.
            </>
          )
        }
      />

      {rows !== null && rows.length > 0 && (
        <TableFooter
          shown={rows.length}
          total={total}
          population={anyFilter ? 'matching sessions' : 'sessions'}
          offset={offset}
          limit={PAGE}
          onOffset={setOffset}
        />
      )}

      {anyLive && (
        <p className="pt-2 text-xs text-muted-foreground">
          <span className="live-dot mr-1 align-middle" />
          {live} session{live === 1 ? '' : 's'} live — refreshing every {POLL_MS / 1000}{' '}
          seconds. Polling stops when nothing is live.
        </p>
      )}
    </div>
  )
}
